import { normalizeAppearanceOverride } from "../appearance";
import type { AppearanceOverride } from "../types";
import {
  LOCAL_PORTFOLIO_STORAGE_SCHEMA_VERSION,
  MAX_ASSET_REFERENCE_LENGTH,
  MAX_IDENTIFIER_LENGTH,
  MAX_INSTRUMENT_NAME_LENGTH,
  MAX_NICKNAME_LENGTH,
  MAX_STORED_PORTFOLIO_HOLDINGS,
  MAX_TICKER_LENGTH,
  PORTFOLIO_SCHEMA_VERSION,
} from "./constants";
import {
  isDecimalString,
  isNonNegativeDecimalString,
  isPositiveDecimalString,
  normalizeDecimalString,
  type DecimalString,
  type NonNegativeDecimalString,
  type PositiveDecimalString,
} from "./decimal";
import { normalizeInstrumentTicker } from "./identity";
import {
  isIsoCalendarDate,
  isRfc3339Timestamp,
  toCanonicalUtcTimestamp,
  type HoldingCustomizationV1,
  type HoldingV1,
  type LocalPortfolioRecordV1,
  type ManualQuoteV1,
  type QuoteStatusV1,
} from "./schema";
import {
  validateLocalPortfolioRecordV1,
  type PortfolioValidationIssue,
} from "./validation";

export const LEGACY_PORTFOLIO_STORAGE_KEY =
  "banryeojusik.portfolio.v1" as const;
export const LEGACY_APPEARANCE_STORAGE_KEY =
  "banryeojusik.appearance.v1" as const;

export interface LegacyMigrationDependencies {
  readonly now: () => Date;
}

export type LegacyMigrationFailureReason =
  | "missing_legacy_portfolio"
  | "corrupt_legacy_portfolio"
  | "invalid_legacy_portfolio"
  | "corrupt_legacy_appearance"
  | "invalid_legacy_appearance"
  | "invalid_clock"
  | "target_validation_failed";

export type LegacyMigrationResult =
  | {
      readonly success: true;
      readonly record: LocalPortfolioRecordV1;
    }
  | {
      readonly success: false;
      readonly reason: LegacyMigrationFailureReason;
      readonly issues?: readonly PortfolioValidationIssue[];
    };

type JsonRecord = Record<string, unknown>;

const QUOTE_STATUSES = new Set<QuoteStatusV1>([
  "valid",
  "stale",
  "missing",
  "new",
]);
const CUSTOMIZATION_FIELDS = [
  "paletteId",
  "bodyColor",
  "accentColor",
  "patternId",
  "hatId",
  "nickname",
] as const;
const MAX_LEGACY_PORTFOLIO_RAW_LENGTH = 2_000_000;
const MAX_LEGACY_APPEARANCE_RAW_LENGTH = 512_000;
const MAX_LEGACY_POSITION_KEYS = 32;
const MAX_LEGACY_APPEARANCE_ENTRIES =
  MAX_STORED_PORTFOLIO_HOLDINGS * 4;
const MAX_LEGACY_CUSTOMIZATION_KEYS = 16;
const MAX_LEGACY_TIMESTAMP_LENGTH = 64;

interface LegacyAppearanceIndex {
  readonly exact: ReadonlyMap<string, JsonRecord>;
  readonly ticker: ReadonlyMap<string, JsonRecord>;
}

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseJsonRecord(
  raw: string,
): { readonly success: true; readonly value: JsonRecord } | {
  readonly success: false;
} {
  try {
    const value: unknown = JSON.parse(raw);
    return isRecord(value) ? { success: true, value } : { success: false };
  } catch {
    return { success: false };
  }
}

function hasBoundedString(
  value: unknown,
  maximumLength: number,
  allowEmpty = false,
): value is string {
  return (
    typeof value === "string" &&
    value.length <= maximumLength &&
    (allowEmpty || value.length > 0)
  );
}

function preflightLegacyPositions(positions: unknown[]): boolean {
  if (
    positions.length === 0 ||
    positions.length > MAX_STORED_PORTFOLIO_HOLDINGS
  ) {
    return false;
  }

  for (let index = 0; index < positions.length; index += 1) {
    const candidate = positions[index];
    if (
      !isRecord(candidate) ||
      Object.keys(candidate).length > MAX_LEGACY_POSITION_KEYS ||
      !hasBoundedString(candidate.instrumentId, MAX_IDENTIFIER_LENGTH) ||
      !hasBoundedString(candidate.ticker, MAX_TICKER_LENGTH) ||
      !hasBoundedString(candidate.name, MAX_INSTRUMENT_NAME_LENGTH) ||
      (candidate.quoteStatus !== undefined &&
        !hasBoundedString(candidate.quoteStatus, 16))
    ) {
      return false;
    }
  }

  return true;
}

function preflightCustomization(value: JsonRecord): boolean {
  if (Object.keys(value).length > MAX_LEGACY_CUSTOMIZATION_KEYS) {
    return false;
  }

  const assetFields = [
    value.paletteId,
    value.patternId,
    value.hatId,
  ];
  if (
    assetFields.some(
      (field) =>
        field !== undefined &&
        !hasBoundedString(field, MAX_ASSET_REFERENCE_LENGTH),
    )
  ) {
    return false;
  }

  for (const color of [value.bodyColor, value.accentColor]) {
    if (
      color !== undefined &&
      !hasBoundedString(color, 7)
    ) {
      return false;
    }
  }

  return (
    value.nickname === undefined ||
    hasBoundedString(value.nickname, MAX_NICKNAME_LENGTH, true)
  );
}

function buildLegacyAppearanceIndex(
  appearances: JsonRecord,
): { readonly success: true; readonly index: LegacyAppearanceIndex } | {
  readonly success: false;
} {
  const keys = Object.keys(appearances);
  if (keys.length > MAX_LEGACY_APPEARANCE_ENTRIES) {
    return { success: false };
  }

  const exact = new Map<string, JsonRecord>();
  const ticker = new Map<string, JsonRecord>();
  for (const key of keys) {
    const value = appearances[key];
    if (
      !hasBoundedString(key, MAX_IDENTIFIER_LENGTH) ||
      !isRecord(value) ||
      !preflightCustomization(value)
    ) {
      return { success: false };
    }

    exact.set(key, value);
    const normalizedTicker = normalizeInstrumentTicker(key);
    if (
      normalizedTicker.length <= MAX_TICKER_LENGTH &&
      !ticker.has(normalizedTicker)
    ) {
      ticker.set(normalizedTicker, value);
    }
  }

  return {
    success: true,
    index: { exact, ticker },
  };
}

function expandExponential(value: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(value);
  if (!match) {
    return value;
  }

  const sign = match[1] ?? "";
  const integer = match[2] ?? "0";
  const fraction = match[3] ?? "";
  const exponent = Number(match[4]);
  const digits = `${integer}${fraction}`;
  const decimalIndex = integer.length + exponent;

  if (decimalIndex <= 0) {
    return `${sign}0.${"0".repeat(-decimalIndex)}${digits}`;
  }
  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${"0".repeat(decimalIndex - digits.length)}`;
  }
  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
}

/**
 * Legacy snapshots stored JS numbers. This converts their shortest round-trip
 * representation into the bounded decimal-string contract. A tiny binary
 * floating-point tail may be rounded to 12 fractional digits, but a meaningful
 * out-of-contract value fails the whole migration instead of being dropped.
 */
function legacyNumberToDecimal(value: number): DecimalString | null {
  if (!Number.isFinite(value)) {
    return null;
  }

  const normalizedValue = Object.is(value, -0) ? 0 : value;
  let expanded = expandExponential(String(normalizedValue));
  if (isDecimalString(expanded)) {
    return normalizeDecimalString(expanded);
  }

  if (Math.abs(normalizedValue) >= 1e21) {
    return null;
  }

  expanded = normalizedValue.toFixed(12);
  if (expanded.includes(".")) {
    expanded = expanded.replace(/0+$/, "").replace(/\.$/, "");
  }
  if (expanded === "-0" || expanded === "") {
    expanded = "0";
  }
  if (!isDecimalString(expanded)) {
    return null;
  }

  const rounded = Number(expanded);
  const tolerance = Math.max(
    Number.EPSILON * Math.max(1, Math.abs(normalizedValue)) * 8,
    5e-13,
  );
  if (
    Math.abs(rounded - normalizedValue) > tolerance ||
    (normalizedValue !== 0 && rounded === 0)
  ) {
    return null;
  }

  return normalizeDecimalString(expanded);
}

function requiredDecimal(
  value: unknown,
  predicate: (candidate: unknown) => boolean,
): DecimalString | null {
  if (typeof value !== "number") {
    return null;
  }
  const decimal = legacyNumberToDecimal(value);
  return decimal && predicate(decimal) ? decimal : null;
}

function optionalDecimal(
  value: unknown,
  predicate: (candidate: unknown) => boolean,
): { readonly success: true; readonly value?: DecimalString } | {
  readonly success: false;
} {
  if (value === undefined) {
    return { success: true };
  }
  const decimal = requiredDecimal(value, predicate);
  return decimal
    ? { success: true, value: decimal }
    : { success: false };
}

function stableHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function idFragment(value: string, maxLength: number): string {
  const normalized = value
    .normalize("NFKC")
    .replace(/[^A-Za-z0-9._:-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[_:.-]+|[_:.-]+$/g, "");
  return (normalized || "unknown").slice(0, maxLength);
}

/**
 * The legacy instrument id remains source identity only. The durable v1 id is
 * deterministic, collision-resistant for a portfolio, and stable across
 * retries because it also includes the original array index.
 */
export function createLegacyHoldingId(
  portfolioId: string,
  instrumentId: string,
  index: number,
): string {
  const source = `${portfolioId}\u0000${instrumentId}\u0000${index}`;
  return [
    "holding",
    idFragment(portfolioId, 24),
    idFragment(instrumentId, 48),
    String(index),
    stableHash(source),
  ].join(":");
}

function normalizedCustomization(
  input: unknown,
): { readonly success: true; readonly value: AppearanceOverride } | {
  readonly success: false;
} {
  if (input === undefined) {
    return { success: true, value: {} };
  }
  if (!isRecord(input)) {
    return { success: false };
  }

  const value = normalizeAppearanceOverride(input);
  for (const field of CUSTOMIZATION_FIELDS) {
    if (input[field] !== undefined && value[field] === undefined) {
      return { success: false };
    }
  }
  if (
    typeof input.nickname === "string" &&
    value.nickname !== input.nickname.trim()
  ) {
    return { success: false };
  }

  return { success: true, value };
}

function mergeCustomization(
  appearances: LegacyAppearanceIndex,
  ticker: string,
  instrumentId: string,
): { readonly success: true; readonly value?: HoldingCustomizationV1 } | {
  readonly success: false;
} {
  const tickerOverride = normalizedCustomization(
    appearances.exact.get(ticker) ?? appearances.ticker.get(ticker),
  );
  const instrumentOverride = normalizedCustomization(
    appearances.exact.get(instrumentId),
  );
  if (!tickerOverride.success || !instrumentOverride.success) {
    return { success: false };
  }

  const merged = {
    ...tickerOverride.value,
    ...instrumentOverride.value,
  };
  return Object.keys(merged).length > 0
    ? {
        success: true,
        value: merged as HoldingCustomizationV1,
      }
    : { success: true };
}

interface MigratedPosition {
  readonly holding: HoldingV1;
  readonly quote: ManualQuoteV1;
}

type PositionMigrationResult =
  | { readonly success: true; readonly value: MigratedPosition }
  | { readonly success: false; readonly reason: "portfolio" | "appearance" };

function migratePosition(
  candidate: unknown,
  index: number,
  portfolioId: string,
  portfolioCurrency: string,
  timestamp: string,
  appearances: LegacyAppearanceIndex,
): PositionMigrationResult {
  if (!isRecord(candidate)) {
    return { success: false, reason: "portfolio" };
  }

  const instrumentId =
    typeof candidate.instrumentId === "string"
      ? candidate.instrumentId
      : "";
  const ticker =
    typeof candidate.ticker === "string"
      ? normalizeInstrumentTicker(candidate.ticker)
      : "";
  const name = typeof candidate.name === "string" ? candidate.name : "";
  if (
    !instrumentId.trim() ||
    !ticker ||
    !name ||
    name !== name.trim()
  ) {
    return { success: false, reason: "portfolio" };
  }

  const quantity = optionalDecimal(
    candidate.quantity,
    isPositiveDecimalString,
  );
  const averagePrice = optionalDecimal(
    candidate.averagePrice,
    isNonNegativeDecimalString,
  );
  const currentPrice = optionalDecimal(
    candidate.currentPrice,
    isNonNegativeDecimalString,
  );
  const costBasis = optionalDecimal(
    candidate.costBasis,
    isNonNegativeDecimalString,
  );
  const marketValue = requiredDecimal(
    candidate.marketValue,
    isNonNegativeDecimalString,
  );
  const dailyChangeAmount = optionalDecimal(
    candidate.dailyChangeAmount,
    isDecimalString,
  );
  if (
    !quantity.success ||
    !averagePrice.success ||
    !currentPrice.success ||
    !costBasis.success ||
    !marketValue ||
    !dailyChangeAmount.success
  ) {
    return { success: false, reason: "portfolio" };
  }

  let dailyReturnPct: DecimalString | undefined;
  if (candidate.dailyReturnPct !== null) {
    const migratedReturn = optionalDecimal(
      candidate.dailyReturnPct,
      isDecimalString,
    );
    if (!migratedReturn.success) {
      return { success: false, reason: "portfolio" };
    }
    dailyReturnPct = migratedReturn.value;
  }

  const quoteStatus =
    candidate.quoteStatus === undefined
      ? "new"
      : typeof candidate.quoteStatus === "string" &&
          QUOTE_STATUSES.has(candidate.quoteStatus as QuoteStatusV1)
        ? (candidate.quoteStatus as QuoteStatusV1)
        : null;
  if (!quoteStatus) {
    return { success: false, reason: "portfolio" };
  }

  const resolvedQuantity = quantity.value ?? ("1" as DecimalString);
  let resolvedAveragePrice = averagePrice.value;
  if (
    resolvedAveragePrice === undefined &&
    costBasis.value !== undefined
  ) {
    const numericQuantity = Number(resolvedQuantity);
    const numericCostBasis = Number(costBasis.value);
    resolvedAveragePrice = legacyNumberToDecimal(
      numericCostBasis / numericQuantity,
    ) ?? undefined;
    if (resolvedAveragePrice === undefined) {
      return { success: false, reason: "portfolio" };
    }
  }

  const customization = mergeCustomization(
    appearances,
    ticker,
    instrumentId,
  );
  if (!customization.success) {
    return { success: false, reason: "appearance" };
  }

  const holdingId = createLegacyHoldingId(
    portfolioId,
    instrumentId,
    index,
  );
  const holding: HoldingV1 = {
    holdingId,
    assetKind: "stock",
    ticker,
    name,
    currency:
      portfolioCurrency as NonNullable<HoldingV1["currency"]>,
    quantity: resolvedQuantity as PositiveDecimalString,
    ...(resolvedAveragePrice !== undefined
      ? {
          costBasis: {
            mode: "average" as const,
            averageBuyPrice:
              resolvedAveragePrice as NonNegativeDecimalString,
          },
        }
      : {}),
    ...(customization.value
      ? { customization: customization.value }
      : {}),
    createdAt: timestamp,
    updatedAt: timestamp,
  };

  const quote: ManualQuoteV1 = {
    holdingId,
    quoteStatus,
    ...(currentPrice.value !== undefined
      ? {
          currentPrice: currentPrice.value as NonNegativeDecimalString,
        }
      : {}),
    marketValue: marketValue as NonNegativeDecimalString,
    ...(dailyReturnPct !== undefined
      ? {
          dailyReturnPct,
        }
      : {}),
    ...(dailyChangeAmount.value !== undefined
      ? {
          dailyChangeAmount: dailyChangeAmount.value as DecimalString,
        }
      : {}),
  };

  return { success: true, value: { holding, quote } };
}

/**
 * Atomically converts the two legacy payloads in memory. It never returns a
 * partial document: one malformed position/customization fails the migration.
 * Persistence and exact raw backup are intentionally handled by the storage
 * layer before this function is called.
 */
export function migrateLegacyPortfolio(
  rawPortfolio: string | null,
  rawAppearance: string | null,
  dependencies: LegacyMigrationDependencies,
): LegacyMigrationResult {
  if (rawPortfolio === null) {
    return { success: false, reason: "missing_legacy_portfolio" };
  }
  if (rawPortfolio.length > MAX_LEGACY_PORTFOLIO_RAW_LENGTH) {
    return { success: false, reason: "invalid_legacy_portfolio" };
  }
  if (
    rawAppearance !== null &&
    rawAppearance.length > MAX_LEGACY_APPEARANCE_RAW_LENGTH
  ) {
    return { success: false, reason: "invalid_legacy_appearance" };
  }

  const parsedPortfolio = parseJsonRecord(rawPortfolio);
  if (!parsedPortfolio.success) {
    return { success: false, reason: "corrupt_legacy_portfolio" };
  }

  const legacy = parsedPortfolio.value;
  let positions: unknown[];
  try {
    if (
      !Array.isArray(legacy.positions) ||
      legacy.positions.length === 0 ||
      legacy.positions.length > MAX_STORED_PORTFOLIO_HOLDINGS
    ) {
      return { success: false, reason: "invalid_legacy_portfolio" };
    }
    positions = legacy.positions;
  } catch {
    return { success: false, reason: "invalid_legacy_portfolio" };
  }

  let appearances: JsonRecord = {};
  if (rawAppearance !== null) {
    const parsedAppearance = parseJsonRecord(rawAppearance);
    if (!parsedAppearance.success) {
      return { success: false, reason: "corrupt_legacy_appearance" };
    }
    appearances = parsedAppearance.value;
  }

  let portfolioId: string;
  let currency: string;
  let farmUnit: DecimalString | null;
  let appearanceIndex: LegacyAppearanceIndex;
  try {
    const indexedAppearances = buildLegacyAppearanceIndex(appearances);
    if (
      !preflightLegacyPositions(positions) ||
      !indexedAppearances.success ||
      !hasBoundedString(legacy.portfolioId, MAX_IDENTIFIER_LENGTH) ||
      legacy.portfolioId !== legacy.portfolioId.trim() ||
      !hasBoundedString(legacy.currency, 3) ||
      !hasBoundedString(legacy.asOf, MAX_LEGACY_TIMESTAMP_LENGTH) ||
      !hasBoundedString(legacy.marketDate, 10)
    ) {
      return {
        success: false,
        reason: !indexedAppearances.success
          ? "invalid_legacy_appearance"
          : "invalid_legacy_portfolio",
      };
    }

    portfolioId = legacy.portfolioId;
    currency = legacy.currency.trim().toUpperCase();
    farmUnit = requiredDecimal(
      legacy.farmUnit,
      isPositiveDecimalString,
    );
    appearanceIndex = indexedAppearances.index;
  } catch {
    return { success: false, reason: "invalid_legacy_portfolio" };
  }
  if (
    !currency ||
    !farmUnit ||
    !isRfc3339Timestamp(legacy.asOf) ||
    !isIsoCalendarDate(legacy.marketDate)
  ) {
    return { success: false, reason: "invalid_legacy_portfolio" };
  }

  let now: string | null;
  try {
    const value = dependencies.now();
    now = value instanceof Date ? toCanonicalUtcTimestamp(value) : null;
  } catch {
    return { success: false, reason: "invalid_clock" };
  }
  if (!now) {
    return { success: false, reason: "invalid_clock" };
  }

  const migrated: MigratedPosition[] = [];
  for (let index = 0; index < positions.length; index += 1) {
    let result: PositionMigrationResult;
    try {
      result = migratePosition(
        positions[index],
        index,
        portfolioId,
        currency,
        now,
        appearanceIndex,
      );
    } catch {
      return {
        success: false,
        reason: "invalid_legacy_portfolio",
      };
    }
    if (!result.success) {
      return {
        success: false,
        reason:
          result.reason === "appearance"
            ? "invalid_legacy_appearance"
            : "invalid_legacy_portfolio",
      };
    }
    migrated.push(result.value);
  }

  const record: LocalPortfolioRecordV1 = {
    storageSchemaVersion: LOCAL_PORTFOLIO_STORAGE_SCHEMA_VERSION,
    document: {
      schemaVersion: PORTFOLIO_SCHEMA_VERSION,
      portfolioId,
      currency: currency as LocalPortfolioRecordV1["document"]["currency"],
      farmUnit: farmUnit as PositiveDecimalString,
      holdings: migrated.map(({ holding }) => holding),
      createdAt: now,
      updatedAt: now,
    },
    manualQuotes: {
      schemaVersion: PORTFOLIO_SCHEMA_VERSION,
      portfolioId,
      currency: currency as LocalPortfolioRecordV1["document"]["currency"],
      asOf: legacy.asOf,
      marketDate: legacy.marketDate,
      quotes: migrated.map(({ quote }) => quote),
    },
  };

  const validated = validateLocalPortfolioRecordV1(record);
  return validated.success
    ? { success: true, record: validated.data }
    : {
        success: false,
        reason:
          validated.issues.some((issue) =>
            issue.fieldPath.includes("customization"),
          )
            ? "invalid_legacy_appearance"
            : "target_validation_failed",
        issues: validated.issues,
      };
}
